import type { FC } from 'react';
import { Collapse, Spin } from 'antd';
import type { FormInstance } from 'antd';

import type { PPGRow } from '../../types/promoMetrics';
import type { ParsedPromoOptions } from '../../config/parsePromoConfig';
import PromoMetaForm from './PromoMetaForm';
import PromoMetricsSection from './PromoMetricsSection';

const COLLAPSE_STYLE = {
    backgroundColor: '#FFF',
    borderRadius: '0.5rem',
    border: '1px solid #F0F0F0',
    background: '#FFF',
};

const USER_EDITED_LEGEND_STYLE = {
    display: 'inline-block',
    width: 12,
    height: 12,
    borderRadius: 2,
    backgroundColor: '#DBEAFE',
    border: '1px solid #60A5FA',
} as const;

const CASCADED_LEGEND_STYLE = {
    display: 'inline-block',
    width: 12,
    height: 12,
    borderRadius: 2,
    backgroundColor: '#FEF3C7',
    border: '1px solid #FBBF24',
} as const;

interface PromoDesignerProps {
    promoForm: FormInstance;
    instanceId: string;
    ppgData: PPGRow[];
    baseline: Map<string, Record<string, number>>;
    onMetricChange: (ppgId: string, metricKey: string, newValue: number) => void;
    onFormValuesChange: (changed: Record<string, unknown>) => void;
    onAddProducts: (ppgIds: number[]) => void;
    isAddProductsDisabled: boolean;
    isLoadingRecommendedValues: boolean;
    isLoadingNextPage: boolean;
    hasMoreRecommendedValues: boolean | undefined;
    fetchNextRecommendedPage: () => void;
    hasProducts: boolean;
    promoType: string | undefined;
    existingProductIds: number[];
    eventId: string;
    isLoadingPromoData: boolean;
    promotionId: string | null;
    parsedOptions?: ParsedPromoOptions;
    productOptions: { label: string; value: string }[];
    isLoadingProducts: boolean;
    onRefreshUplift: () => Promise<void>;
    isUpliftPending: boolean;
    isUpliftApplicable: boolean;
}

const PromoDesigner: FC<PromoDesignerProps> = ({
    promoForm,
    instanceId,
    ppgData,
    baseline,
    onMetricChange,
    onFormValuesChange,
    onAddProducts,
    isAddProductsDisabled,
    isLoadingRecommendedValues,
    isLoadingNextPage,
    hasMoreRecommendedValues,
    fetchNextRecommendedPage,
    hasProducts,
    promoType,
    existingProductIds,
    eventId,
    isLoadingPromoData,
    promotionId,
    parsedOptions,
    productOptions,
    isLoadingProducts,
    onRefreshUplift,
    isUpliftPending,
    isUpliftApplicable,
}) => {
    if (promotionId && isLoadingPromoData) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spin size="default" />
            </div>
        );
    }

    return (
        <div>
            <div className="gap-y-4 flex flex-col">
                <Collapse expandIconPosition="end" defaultActiveKey={['1']} style={COLLAPSE_STYLE}>
                    <Collapse.Panel header={<p className="text-base font-semibold">{'Add Promo Details'}</p>} key="1">
                        <PromoMetaForm
                            form={promoForm}
                            instanceId={instanceId}
                            promotionId={promotionId}
                            parsedOptions={parsedOptions}
                            onFormValuesChange={onFormValuesChange}
                            isUpliftPending={isUpliftPending}
                        />
                    </Collapse.Panel>
                </Collapse>
                <Collapse expandIconPosition="end" defaultActiveKey={['1']} style={COLLAPSE_STYLE}>
                    <Collapse.Panel header={<p className="text-base font-semibold">{'Tactics'}</p>} key="1">
                        <PromoMetricsSection
                            ppgData={ppgData}
                            onMetricChange={onMetricChange}
                            baseline={baseline}
                            onAddProducts={onAddProducts}
                            isAddProductsDisabled={isAddProductsDisabled}
                            isLoadingRecommendedValues={isLoadingRecommendedValues}
                            isLoadingNextPage={isLoadingNextPage}
                            hasMoreRecommendedValues={hasMoreRecommendedValues}
                            fetchNextRecommendedPage={fetchNextRecommendedPage}
                            hasProducts={hasProducts}
                            promoType={promoType}
                            instanceId={instanceId}
                            existingProductIds={existingProductIds}
                            productOptions={productOptions}
                            isLoadingProducts={isLoadingProducts}
                            onRefreshUplift={onRefreshUplift}
                            isUpliftPending={isUpliftPending}
                            isUpliftApplicable={isUpliftApplicable}
                        />
                    </Collapse.Panel>
                </Collapse>
                <div className="flex justify-end gap-x-3">
                    <div className="flex items-center gap-x-4 text-xs mr-4">
                        <span className="flex items-center gap-x-1.5">
                            <span style={USER_EDITED_LEGEND_STYLE} />
                            User edited
                        </span>
                        <span className="flex items-center gap-x-1.5">
                            <span style={CASCADED_LEGEND_STYLE} />
                            Cascaded
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

PromoDesigner.displayName = 'PromoDesigner';

export default PromoDesigner;
